import { useRef, useEffect } from 'react'
import { Modal as AntModal } from 'antd'
import {
  UndoOutlined,
  RedoOutlined,
  EyeOutlined,
  DesktopOutlined,
  MobileOutlined,
} from '@ant-design/icons'
import { Button, IconButton } from '@/components/ui'
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor'
import { buildMergeTags, type MergeTagOptions } from '@/lib/mergeTags'

interface EmailEditorModalProps {
  open: boolean
  onClose: () => void
  onSave: (html: string, designJson: Record<string, unknown>) => void
  initialHtml?: string
  initialDesignJson?: Record<string, unknown>
  mergeTagOptions?: MergeTagOptions
}

const EmailEditorModal = ({
  open,
  onClose,
  onSave,
  initialDesignJson,
  mergeTagOptions,
}: EmailEditorModalProps) => {
  const emailEditorRef = useRef<EditorRef | null>(null)

  const handleEditorLoad: EmailEditorProps['onLoad'] = (unlayer) => {
    // Load initial design if provided
    if (initialDesignJson && Object.keys(initialDesignJson).length > 0) {
      unlayer.loadDesign(initialDesignJson as Parameters<typeof unlayer.loadDesign>[0])
    }
  }

  const handleEditorReady: EmailEditorProps['onReady'] = () => {
    // Editor is ready
    console.log('Email editor ready')
  }

  const handleSave = () => {
    const unlayer = emailEditorRef.current?.editor
    if (!unlayer) return

    unlayer.exportHtml((data) => {
      const { design, html } = data
      onSave(html, design as Record<string, unknown>)
    })
  }

  const handleUndo = () => {
    // Unlayer doesn't expose undo directly, but we can implement via design history
    // For now, this is a placeholder
  }

  const handleRedo = () => {
    // Unlayer doesn't expose redo directly
    // For now, this is a placeholder
  }

  const handlePreview = () => {
    const unlayer = emailEditorRef.current?.editor
    if (unlayer) {
      unlayer.showPreview({ device: 'desktop' })
    }
  }

  const handleSetDesktopMode = () => {
    const unlayer = emailEditorRef.current?.editor
    if (unlayer) {
      unlayer.setAppearance({
        theme: 'light',
      })
    }
  }

  const handleSetMobileMode = () => {
    const unlayer = emailEditorRef.current?.editor
    if (unlayer) {
      unlayer.showPreview({ device: 'mobile' })
    }
  }

  // Reset editor when modal opens with new design
  useEffect(() => {
    if (open && emailEditorRef.current?.editor && initialDesignJson) {
      emailEditorRef.current.editor.loadDesign(
        initialDesignJson as Parameters<typeof emailEditorRef.current.editor.loadDesign>[0]
      )
    }
  }, [open, initialDesignJson])

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      width="100%"
      style={{ top: 0, padding: 0, maxWidth: '100vw' }}
      styles={{
        body: { padding: 0, height: 'calc(100vh - 55px)' },
        content: { borderRadius: 0 },
      }}
      closable={false}
      className="email-editor-modal"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-white">
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium">Email Editor</span>
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Draft</span>
          </div>

          <div className="flex items-center gap-4">
            {/* Undo/Redo */}
            <div className="flex items-center gap-1 border-r pr-4">
              <IconButton
                icon={<UndoOutlined />}
                onClick={handleUndo}
                ariaLabel="Undo"
                title="Undo"
              />
              <IconButton
                icon={<RedoOutlined />}
                onClick={handleRedo}
                ariaLabel="Redo"
                title="Redo"
              />
            </div>

            {/* Preview/Device toggle */}
            <div className="flex items-center gap-1 border-r pr-4">
              <IconButton
                icon={<EyeOutlined />}
                onClick={handlePreview}
                ariaLabel="Preview"
                title="Preview"
              />
              <IconButton
                icon={<DesktopOutlined />}
                onClick={handleSetDesktopMode}
                ariaLabel="Desktop view"
                title="Desktop"
              />
              <IconButton
                icon={<MobileOutlined />}
                onClick={handleSetMobileMode}
                ariaLabel="Mobile view"
                title="Mobile"
              />
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-hidden">
          <EmailEditor
            ref={emailEditorRef}
            onLoad={handleEditorLoad}
            onReady={handleEditorReady}
            minHeight="100%"
            options={{
              features: {
                textEditor: {
                  spellChecker: true,
                },
              },
              appearance: {
                theme: 'light',
                panels: {
                  tools: {
                    dock: 'right',
                  },
                },
              },
              tools: {
                image: {
                  enabled: true,
                },
              },
              mergeTags: buildMergeTags(mergeTagOptions),
            }}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-white">
          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Back
          </Button>
        </div>

        {/* Unlayer branding note */}
        <div className="absolute bottom-16 right-6 text-xs text-gray-400">
          Powered by Unlayer Editor
        </div>
      </div>
    </AntModal>
  )
}

export default EmailEditorModal
